import {
  AfterViewInit,
  Directive,
  DestroyRef,
  ElementRef,
  NgZone,
  inject,
} from '@angular/core';
import { FormControl, NgControl } from '@angular/forms';

@Directive({
  selector: '[appQuillListMarkerStyle]',
  standalone: true,
})
export class QuillListMarkerStyleDirective implements AfterViewInit {
  readonly #elementRef = inject(ElementRef<HTMLElement>);
  readonly #destroyRef = inject(DestroyRef);
  readonly #ngControl = inject(NgControl, { optional: true });
  readonly #ngZone = inject(NgZone);
  #editorObserver?: MutationObserver;

  constructor() {
    this.#destroyRef.onDestroy(() => this.#editorObserver?.disconnect());
  }

  ngAfterViewInit(): void {
    this.#ngZone.runOutsideAngular(() => {
      queueMicrotask(() => {
        const editor = this.#findEditor();

        if (!editor) {
          return;
        }

        if (this.#syncListMarkerStyles(editor)) {
          this.#updateControlValue(editor);
        }

        this.#editorObserver = new MutationObserver(() => {
          if (this.#syncListMarkerStyles(editor)) {
            this.#updateControlValue(editor);
          }
        });

        this.#editorObserver.observe(editor, {
          attributes: true,
          attributeFilter: ['class', 'style'],
          childList: true,
          characterData: true,
          subtree: true,
        });
      });
    });
  }

  #findEditor(): HTMLElement | null {
    return this.#elementRef.nativeElement.querySelector('.ql-editor');
  }

  #syncListMarkerStyles(editor: HTMLElement): boolean {
    let changed = false;

    editor.querySelectorAll<HTMLLIElement>('li').forEach((listItem) => {
      const markerSource = this.#findFirstTextElement(listItem);

      if (!markerSource || markerSource === listItem) {
        changed = this.#clearMarkerStyle(listItem) || changed;
        return;
      }

      const sourceStyle = getComputedStyle(markerSource);
      changed =
        this.#setMarkerStyle(listItem, {
          color: sourceStyle.color,
          fontSize: sourceStyle.fontSize,
          fontStyle: sourceStyle.fontStyle,
          fontWeight: sourceStyle.fontWeight,
        }) || changed;
    });

    return changed;
  }

  #findFirstTextElement(element: HTMLElement): HTMLElement | null {
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
        return element;
      }

      if (!(child instanceof HTMLElement) || child.classList.contains('ql-ui')) {
        continue;
      }

      const nestedElement = this.#findFirstTextElement(child);

      if (nestedElement) {
        return nestedElement;
      }
    }

    return element.textContent?.trim() ? element : null;
  }

  #setMarkerStyle(
    listItem: HTMLLIElement,
    styles: {
      color: string;
      fontSize: string;
      fontStyle: string;
      fontWeight: string;
    },
  ): boolean {
    const styleMap = {
      '--quill-list-marker-color': styles.color,
      '--quill-list-marker-font-size': styles.fontSize,
      '--quill-list-marker-font-style': styles.fontStyle,
      '--quill-list-marker-font-weight': styles.fontWeight,
    };
    let changed = false;

    Object.entries(styleMap).forEach(([property, value]) => {
      if (listItem.style.getPropertyValue(property) !== value) {
        listItem.style.setProperty(property, value);
        changed = true;
      }
    });

    if (!listItem.classList.contains('ql-list-marker-styled')) {
      listItem.classList.add('ql-list-marker-styled');
      changed = true;
    }

    return changed;
  }

  #clearMarkerStyle(listItem: HTMLLIElement): boolean {
    const properties = [
      '--quill-list-marker-color',
      '--quill-list-marker-font-size',
      '--quill-list-marker-font-style',
      '--quill-list-marker-font-weight',
    ];
    let changed = false;

    properties.forEach((property) => {
      if (listItem.style.getPropertyValue(property)) {
        listItem.style.removeProperty(property);
        changed = true;
      }
    });

    if (listItem.classList.contains('ql-list-marker-styled')) {
      listItem.classList.remove('ql-list-marker-styled');
      changed = true;
    }

    return changed;
  }

  #updateControlValue(editor: HTMLElement): void {
    const control = this.#ngControl?.control as FormControl<string | null> | null;

    if (!control || control.value === editor.innerHTML) {
      return;
    }

    control.setValue(editor.innerHTML, {
      emitEvent: false,
      emitModelToViewChange: false,
    });
  }
}
